import { getLastAuditLog, getLastRedactExport } from '../selectors';
import * as actions from '../actions';
import { GlobalSettings, HighlightedOverlay, Tag } from '../models';
import { fetchCaseMediaSuccess } from './fetchCaseMedia';
import { markTdoChanged } from './tdoChanged';
import { findLast, findLastIndex, get, size, uniqBy } from 'lodash';
import { SummaryTDO } from '@redact-modules/mainPage/models';
import type { TaskStatus } from 'veritone-types';
import { cleanDeleteOverlayCache } from '../helpers/cache-helper';
import { defaultState, MainStore } from '../store';
import { getUDRGroupIntervalBasedSegments } from '@common/web-worker/selectors/clusters';
import {
  ROUTE_DOCUMENT_DETAILS,
  ROUTE_HOME,
  ROUTE_MEDIA_DETAILS,
} from '@common-modules/routing';
import {
  IN_SAVE_CHANGES,
  OUT_FETCH_REDACT_TDO_SUCCESS,
  // OUT_FETCH_DOWNLOAD_ASSET_SUCCESS,
  OUT_POLL_DOWNLOAD_ASSET_SUCCESS,
  OUT_POLL_DOWNLOAD_ASSET_FAILED,
  OUT_CREATE_JOB_FAIL,
  OUT_PROGRESS,
  OUT_SPEAKER_SEPARATION_FETCH_SETTLED,
  OUT_TRANSCRIPTION_FETCH_FAILED,
  FETCH_DOWNLOAD_ASSET_FAILURE,
  FETCH_REDACT_TDO_FAILURE,
  SAVE_MEDIA_DATA_SUCCESS,
} from '@worker';
import {
  CREATE_JOB_FACES_ACTION,
  CREATE_JOB_FACES_FAILURE,
  ON_CHANGE_DETECT_OBJECTS,
  SHOW_DETECT_OPTIONS_DIALOG,
  CREATE_JOB_FACES,
  DETECT_OPTIONS,
} from '@common-modules/facesTabModule';
import {
  PROCESS_ENGINE_REQUEST as PROCESS_ENGINE_TRANSCRIPTION_REQUEST,
  CREATE_ENGINE_JOB_FAILURE as CREATE_ENGINE_JOB_TRANSCRIPTION_FAILURE,
  PROCESS_ENGINE_REQUEST_SUCCESS as PROCESS_ENGINE_TRANSCRIPTION_REQUEST_SUCCESS,
} from '@common-modules/engines/transcription/actions';
import {
  selectComment,
  removeComment,
  addNewComment,
  fetchMediaCommentsUserSuccess,
  fetchMediaComments,
  updateCommentSuccess,
  fetchMediaCommentsSuccess,
  setMediaCommentDraft,
  clearMediaCommentDraft,
  setEditingMediaComment,
  fetchMediaUserSuccess,
  basicUserQueryIsSupported,
  selectCommentsOnTimeline,
  updateStatusHideComment,
} from './mediaComments';
import { DEFAULT_GLOBAL_SETTINGS } from '@helpers/constants';

import { sagaIntl } from '@common/i18n';
import { Action, createReducer } from '@reduxjs/toolkit';
import { produce, produceWithPatches, WritableDraft } from 'immer';
import getLastRedactedFile from '@helpers/getLastRedactedFile';
import { FETCH_INTEGRATION_INFO_SUCCESS } from '@helpers/externalIntegrationApi';
import {
  DEFAULT_REDACTION_STATUS_LABEL,
  getBuiltInRedactionStatusTagWriteFields,
  getRedactionStatusIdentity,
  mergeRedactionStatusTagFields,
  type RedactionStatusTagWriteFields,
} from '../redactionStatuses';

const getOriginalFileStatus = (details?: SummaryTDO['details']) => {
  const tag = details?.tags?.find(
    (tag) => tag?.value === actions.IN_REDACTION_TAG_KEY
  );
  return (
    (tag ? getRedactionStatusIdentity(tag) : undefined) ||
    DEFAULT_REDACTION_STATUS_LABEL
  );
};

const onDeleteUDRSegment = (
  state: WritableDraft<MainStore>,
  action: {
    type: string;
    payload: actions.DeleteSegmentPayload;
  }
) => {
  const { groupId } = action.payload;
  const newState = onUpdateDeletedOverlaysCache(state, action);

  const { selectedUDRGroupId } = newState.udrsState;

  if (groupId === selectedUDRGroupId) {
    return produce(newState, (draft) => {
      draft.udrsState.selectedUDRGroupId = undefined;
    });
  }

  return newState;
};

const onDeleteUDRGroup = (
  state: WritableDraft<MainStore>,
  action: {
    type: string;
    payload: { boundingBox: { groupId: string } };
  }
) => {
  const { boundingBox } = action.payload;
  const groupId = boundingBox.groupId;
  const newState = onUpdateDeletedOverlaysCache(state, action);

  const { selectedUDRGroupId } = newState.udrsState;

  if (groupId === selectedUDRGroupId) {
    return produce(newState, (draft) => {
      draft.udrsState.selectedUDRGroupId = undefined;
    });
  }

  return newState;
};

const onUpdateDeletedOverlaysCache = (
  state: WritableDraft<MainStore>,
  action: Action<any>
) => {
  const intl = sagaIntl();
  const cleanedDeletedOverlaysCache = cleanDeleteOverlayCache(
    state.deletedOverlaysCache
  );

  const [newState, patches, inversePatches] = produceWithPatches(
    state,
    (draft) => {
      draft.deletedOverlaysCache = cleanedDeletedOverlaysCache;
    }
  );

  const updateTimestamp = new Date().getTime();

  switch (true) {
    case actions.DELETE_CLUSTER.match(action):
      // CTODO there might be more we need to do here
      return produce(newState, (draft) => {
        draft.history.past.push({
          patches,
          inversePatches,
          metadata: {
            description: intl.formatMessage({
              id: 'deleteCluster',
              defaultMessage: 'delete group',
            }),
          },
        });
        draft.history.future = [];
        markTdoChanged(draft);
      });

    case actions.DELETE_UDR_OVERLAY.match(action):
      return produce(newState, (draft) => {
        draft.deletedOverlaysCache.overlayIdsMap[
          action.payload.boundingBox.id
        ] = updateTimestamp;
        draft.history.past.push({
          patches,
          inversePatches,
          metadata: {
            description: intl.formatMessage({
              id: 'deleteUdrOverlay',
              defaultMessage: 'delete udr overlay',
            }),
          },
        });
        draft.history.future = [];
      });
    case actions.DELETE_UDR_IN_FRAME.match(action):
      return produce(newState, (draft) => {
        draft.deletedOverlaysCache.overlayIdsMap[
          action.payload.boundingBox.id
        ] = updateTimestamp;
        draft.history.past.push({
          patches,
          inversePatches,
          metadata: {
            description: intl.formatMessage({
              id: 'deleteUdrInFrame',
              defaultMessage: 'delete udr in frame',
            }),
          },
        });
        draft.history.future = [];
      });
    case actions.DELETE_DETECTION_OVERLAY.match(action):
      return produce(newState, (draft) => {
        const { type } = action.payload.boundingBox;
        draft.deletedOverlaysCache.overlayIdsMap[
          action.payload.boundingBox.id
        ] = updateTimestamp;
        draft.history.past.push({
          patches,
          inversePatches,
          metadata: {
            description: intl.formatMessage(
              {
                id: 'deleteDetectionOverlay',
                defaultMessage: 'delete {type} overlay',
              },
              {
                type,
              }
            ),
          },
        });
        draft.history.future = [];
      });
    case actions.DELETE_DETECTION_IN_FRAME.match(action):
      return produce(newState, (draft) => {
        const { type } = action.payload.boundingBox;
        draft.deletedOverlaysCache.overlayIdsMap[
          action.payload.boundingBox.id
        ] = updateTimestamp;
        draft.history.past.push({
          patches,
          inversePatches,
          metadata: {
            description: intl.formatMessage(
              {
                id: 'deleteDetectionInFrame',
                defaultMessage: 'delete {type} in frame',
              },
              {
                type,
              }
            ),
          },
        });
        draft.history.future = [];
      });

    case actions.DELETE_UDR_SEGMENT.match(action): {
      const { groupId, segmentId, timeMs } = action.payload;

      const boundingPolysGroup = groupId
        ? newState.udrsState.udrAsset.boundingPolys[groupId]
        : null;

      if (groupId && boundingPolysGroup?.series) {
        const segments = getUDRGroupIntervalBasedSegments(
          boundingPolysGroup.series,
          groupId
        );

        // find segment
        let deleteSegment;
        if (segmentId) {
          deleteSegment = segments.find((seg) => seg.id === segmentId);
        } else {
          deleteSegment = segments.find(
            (seg) => seg.startTimeMs <= timeMs && seg.stopTimeMs > timeMs
          );
        }

        const objectIds = deleteSegment?.objectIds || [];

        return produce(newState, (draft) => {
          for (const objectId of objectIds) {
            draft.deletedOverlaysCache.overlayIdsMap[objectId] =
              updateTimestamp;
          }
          draft.history.past.push({
            patches,
            inversePatches,
            metadata: {
              description: sagaIntl().formatMessage({
                id: 'deleteUdrSegment',
                defaultMessage: 'delete udr segment',
              }),
            },
          });
          draft.history.future = [];
        });
      }
      break;
    }
    case actions.DELETE_UDR_GROUP.match(action):
      return produce(newState, (draft) => {
        draft.deletedOverlaysCache.overlaySubsegmentIdsMap[
          action.payload.boundingBox.subsegmentId
        ] = updateTimestamp;
        draft.history.past.push({
          patches,
          inversePatches,
          metadata: {
            description: sagaIntl().formatMessage({
              id: 'deleteUdrGroup',
              defaultMessage: 'delete udr group',
            }),
          },
        });
        draft.history.future = [];
      });
    case actions.DELETE_DETECTION_SEGMENT.match(action): {
      const { timeMs, segmentId, groupId, type } = action.payload;
      if (!groupId) {
        return newState;
      }

      const group = state.detectionClusterGroups[groupId];
      if (!group) {
        return newState;
      }

      // search for segment
      let segment;
      if (segmentId) {
        // faster when segmentId is available
        segment = group.segments.find((segment) => segment.id === segmentId);
      } else {
        segment = group.segments.find(
          (segment) =>
            segment.startTimeMs <= timeMs && segment.stopTimeMs > timeMs
        );
      }

      const overlayIdsMapPartialUpdate = segment?.objectIds.reduce<
        Record<string, number>
      >((map, id) => {
        map[id] = updateTimestamp;
        return map;
      }, {});

      return produce(newState, (draft) => {
        draft.deletedOverlaysCache.overlayIdsMap = {
          ...draft.deletedOverlaysCache.overlayIdsMap,
          ...overlayIdsMapPartialUpdate,
        };
        draft.history.past.push({
          patches,
          inversePatches,
          metadata: {
            description: intl.formatMessage(
              {
                id: 'deleteDetectionSegment',
                defaultMessage: 'delete {type} segment',
              },
              {
                type,
              }
            ),
          },
        });
        draft.history.future = [];
      });
    }
    case actions.DELETE_DETECTION_GROUP.match(action): {
      const { groupId, type } = action.payload.boundingBox;
      if (!groupId) {
        return newState;
      }

      const group = state.detectionClusterGroups[groupId];
      if (!group) {
        return newState;
      }

      return produce(newState, (draft) => {
        group.segments.forEach(
          (segment) =>
            // GTODO should this be looping over subsegments?
            (draft.deletedOverlaysCache.overlaySubsegmentIdsMap[segment.id] =
              updateTimestamp)
        );
        draft.history.past.push({
          patches,
          inversePatches,
          metadata: {
            description: intl.formatMessage(
              {
                id: 'deleteDetectionGroup',
                defaultMessage: 'delete {type} group',
              },
              {
                type,
              }
            ),
          },
        });
        draft.history.future = [];
      });
    }
  }
  return newState;
};

const onSetDeletedOverlaysCache = (
  state: WritableDraft<MainStore>,
  action: { payload: typeof state.deletedOverlaysCache }
) => ({
  ...state,
  deletedOverlaysCache: action.payload,
});

export const onFetchTDOSuccess = (
  state: WritableDraft<MainStore>,
  action: { payload: { temporalDataObject: NonNullable<typeof state.tdo> } }
) => ({
  ...state,
  fetchingTdo: false,
  // VE-30516: recorded BEFORE the defaults are merged in below, which is the
  // only moment the distinction exists.
  tdoSettingsFromServer:
    !!action.payload?.temporalDataObject?.details?.settings &&
    Object.keys(action.payload.temporalDataObject.details.settings).length > 0,
  tdo: {
    ...action.payload?.temporalDataObject,
    details: {
      ...action.payload?.temporalDataObject?.details,
      settings: {
        ...DEFAULT_GLOBAL_SETTINGS,
        interpolationMaxStretch: undefined, // preserve as undefined so we don't modify legacy TDOs
        ...action.payload?.temporalDataObject?.details?.settings,
      },
    },
  },
  failedFetchingTdo: false,
  originalFileStatus: getOriginalFileStatus(
    action.payload.temporalDataObject.details || {}
    // get(action, 'payload.temporalDataObject.details', {})
  ),
  trimInterval: get(
    action,
    'payload.temporalDataObject.details.redact.trimInterval',
    undefined
  ),
});

/**
 * Per-file state cleared when the editor moves to a different file without a
 * full ROUTE_MEDIA_DETAILS reset (which already clears all of this) — i.e. the
 * switch to a PDF on ROUTE_DOCUMENT_DETAILS, and RESET_MEDIA_DETAILS_ON_SWITCH.
 */
const switchResetFields = {
  fetchingTdo: false,
  redactionJob: null,
  // creatingRedactionJob: false,
  redactedAssetStarted: false,
  gettingRedactionJobStatus: false,
  redactionJobStatus: false,
  buttonDetectFaceDisable: false,
  buttonTranscriptionDisable: false,
  // The next TDO's fetch has not answered yet, whatever this one's did.
  speakerSeparationFetchSettled: false,
  pendingLockedStatus: null,
  pendingLockedStatusNote: null,
  pendingLockedStatusResponse: null,
  pendingLockedStatusResponseType: null,
  isBannerDismissed: false,
  mediaCommentDrafts: {},
  editingMediaCommentId: null,
  // The playhead scope must not carry into the next TDO, which product
  // explicitly ruled out (VE-25876). The playhead pair goes with it for the
  // same reason: left alone, the first window evaluated against the incoming
  // TDO's collections would straddle two different media timelines.
  onscreenOnlyFilter: false,
  onscreenOnlyFilterUserSet: false,
  currentPosition: 0,
  previousPosition: 0,
  playheadJumped: false,
} as const;

export default createReducer(defaultState, (builder) => {
  builder
    // saveRev survives the reset: a save from the file being left can still
    // land after the route change, and must not match a rev the next file reuses.
    .addCase(ROUTE_HOME, (state) => ({
      ...defaultState,
      saveRev: state.saveRev,
    }))
    .addCase(ROUTE_MEDIA_DETAILS, (state) => ({
      ...defaultState,
      saveRev: state.saveRev,
    }))

    .addCase(actions.PAGE_LOADED, (state) => ({
      ...state,
      loaded: true,
    }))

    .addCase(actions.FETCH_TDO, (state, action) => {
      const requestedTdoId = get(action, 'meta.variables.id') as
        | string
        | undefined;
      const currentTdoId = state.tdo?.id;
      const keepExistingTdo = !!currentTdoId && requestedTdoId === currentTdoId;

      return {
        ...state,
        tdo: keepExistingTdo ? state.tdo : null, // if the requested tdo id is the same as the current tdo id, keep the existing tdo to prevent a flash when sending to external integration
        fetchingTdo: true,
        failedFetchingTdo: false,
        facesSelectedGroup: null,
      };
    })
    .addCase(actions.FETCH_TDO_WITH_FAILURE, (state, action) => ({
      ...state,
      fetchingTdo: false,
      tdo: get(action, 'payload.temporalDataObject', null),
      failedFetchingTdo: false,
    }))
    .addCase(actions.SET_AUDIOWAVES, (state, action) => ({
      ...state,
      audiowaves: action.payload,
    }))
    .addCase(actions.REFRESH_TDO_PRIMARY_ASSET_ID_SUCCESS, (state, action) => {
      if (!state.tdo) {
        return;
      }

      if (state.tdo.primaryAsset) {
        state.tdo.primaryAsset.id =
          action.payload.temporalDataObject.primaryAsset.id;
      } // else {
      // TODO: The original code did this, but it doesn't make any sense to me to refresh the id on
      // a non-existent primaryAsset. Should we really be doing this?
      // state.tdo.primaryAsset = {
      //   id: action.payload.temporalDataObject.primaryAsset.id,
      // };
      // }
    })
    .addCase(actions.REFRESH_TDO_TASKS_SUCCESS, (state, action) => {
      const mergeRecords = get(
        action,
        'payload.temporalDataObject.tasks.records',
        []
      ).concat(get(state, 'tdo.tasks.records', []));
      if (state.tdo) {
        state.tdo.tasks = { records: uniqBy(mergeRecords, 'id') }; // take first occurrence of task id
      }
    })
    .addCase(actions.REDACTED_FILE_COMPLETED, (state, action) => {
      // IMAGE-TODO maybe we can store this info somewhere so less code needed
      const isCurrentTdo = action.payload.tdoId === get(state, 'tdo.id');
      const isImage = String(get(state, 'tdo.primaryAsset.contentType', ''))
        .toLowerCase()
        .startsWith('image/');

      return {
        ...state,
        // For images we auto-switch to Redacted Files tab, which should resets this counter,
        numberOfRedactedFile:
          isCurrentTdo && !isImage
            ? state.numberOfRedactedFile + 1
            : state.numberOfRedactedFile,
      };
    })
    .addCase(actions.SELECT_REDACTED_FILES_TAB, (state) => ({
      ...state,
      mediaDetailsPageSelectedTab: actions.SELECT_REDACTED_FILES_TAB.type,
      numberOfRedactedFile: 0,
    }))
    .addCase(actions.SELECT_EDITOR_TAB, (state) => ({
      ...state,
      mediaDetailsPageSelectedTab: actions.SELECT_EDITOR_TAB.type,
    }))
    .addCase(actions.FACES_SELECT_GROUP_FACES, (state, action) => ({
      ...state,
      facesSelectedGroup: action.payload.groupId,
    }))
    .addCase(actions.FACE_HIGHLIGHT, (state, action) => {
      const { payload } = action;
      let newHighlightedOverlay: HighlightedOverlay | undefined = undefined;

      if (payload !== null && payload.groupId) {
        const { id, timeMs, groupId, type } = payload;
        newHighlightedOverlay = {
          id,
          timeMs,
          groupId,
          type,
        };
      }

      const prevHighlightedOverlay = state.highlightedOverlay;
      const selectedUDRGroupId = state.udrsState.selectedUDRGroupId;

      const shouldResetSelectedUDRGroupId =
        selectedUDRGroupId &&
        selectedUDRGroupId !== newHighlightedOverlay?.groupId &&
        prevHighlightedOverlay?.groupId !== selectedUDRGroupId;

      state.highlightedOverlay = newHighlightedOverlay;

      if (shouldResetSelectedUDRGroupId) {
        state.udrsState.selectedUDRGroupId = undefined;
      }
    })
    .addCase(actions.FACE_HIGHLIGHT_UNSELECT, (state) => {
      const isSomethingSelected = size(state.highlightedOverlay) > 0;

      state.highlightedOverlay = isSomethingSelected
        ? undefined // All logic related to highlightedOverlay is checked to ensure the highlightedOverlay condition exists, so this is safe
        : state.highlightedOverlay;
    })
    .addCase(actions.FACES_SELECT_ENGINE, (state, action) => ({
      ...state,
      facesSelectedEngineId: action.payload.facesSelectedEngineId,
    }))
    // SAVE button
    .addCase(IN_SAVE_CHANGES, (state) => ({
      ...state,
      tdoIsChanged: false,
    }))
    .addCase(SAVE_MEDIA_DATA_SUCCESS, (state, action) => {
      const container = action.payload?.createAsset?.container;
      if (state.tdo?.id && container?.id && container.id !== state.tdo.id) {
        return;
      }
      if (state.tdo && container?.modifiedDateTime) {
        state.tdo.modifiedDateTime = container.modifiedDateTime;
      }
    })
    .addCase(actions.UPDATE_CURRENT_TIME_MEDIA_PLAYER, (state, action) => ({
      ...state,
      // Carry the outgoing sample forward so consumers can ask about the
      // interval [previousPosition, currentPosition] rather than a single
      // instant. The gap between two samples is a `timeupdate` interval, not a
      // frame, and a point query drops any segment narrower than it (VE-25876).
      //
      // Unless the playhead was moved by a seek, in which case no media played
      // through and there is no interval: collapse it to a point. This is
      // decided by the flag rather than by the size of the jump, because a seek
      // and a playback step are not distinguishable by size — 1.5s is both a
      // short seek and one sample at 8x playback.
      //
      // `dragAndDropPositionBar` is checked alongside the flag because a scrub
      // is a RUN of seeks, not one. The flag set at drag start is consumed by
      // the first sample, so every later sample of the same drag would read as
      // playback and widen the window across ground the playhead skipped —
      // over-reporting cards for the length of the drag, until the trailing
      // PLAYER_SEEKED finally collapses it.
      previousPosition:
        state.playheadJumped || state.dragAndDropPositionBar
          ? action.payload.currentPosition
          : state.currentPosition,
      currentPosition: action.payload.currentPosition,
      playheadJumped: false,
    }))
    // App-initiated seeks (clicking a cluster card, keyboard jumps) are
    // dispatched BEFORE the player moves, so flagging here is consumed by the
    // `timeupdate` that follows.
    .addCase(actions.SEEK_MEDIA, (state) => ({
      ...state,
      playheadJumped: true,
    }))
    // The player's own `seeked` event — the catch-all for seeks driven from the
    // player control bar or the timeline (see saga/video.ts onPlayerSeeked). The
    // browser fires `timeupdate` BEFORE `seeked`, so by the time this lands the
    // bad window already exists: collapse it directly rather than flagging for
    // next time. Matters most when paused, where no further `timeupdate` would
    // ever arrive to clear it.
    .addCase(actions.PLAYER_SEEKED, (state) => ({
      ...state,
      previousPosition: state.currentPosition,
      playheadJumped: false,
    }))
    // A pause is not a seek, but it ends playback with a window still open:
    // [previousPosition, currentPosition] describes the last interval that
    // played, and once `timeupdate` stops arriving nothing ever retires it. Left
    // alone the panel keeps listing whatever was on screen up to one sample gap
    // (~250ms, more under load) before the pause — which is exactly when it is
    // least wanted, because pausing IS the operator stopping to judge the frame
    // in front of them. Collapse to the point the playhead actually rests on.
    //
    // No `playheadJumped` here: nothing moved. Resuming starts a fresh interval
    // from this point on the next sample, which is what did play.
    .addCase(actions.PLAYER_PAUSE, (state) => ({
      ...state,
      previousPosition: state.currentPosition,
    }))
    .addCase(actions.START_DRAG_AND_DROP_POSITION_BAR, (state) => ({
      ...state,
      dragAndDropPositionBar: true,
      // Scrubbing the position bar is a seek, not playback.
      playheadJumped: true,
    }))
    .addCase(actions.DROP_POSITION_BAR, (state) => ({
      ...state,
      dragAndDropPositionBar: false,
    }))
    .addCase(actions.FETCH_DELETE_LAST_ASSET_SUCCESS, (state) => {
      if (!state.tdo) {
        return;
      }
      const lastRedactedMediaAsset = getLastRedactedFile(state.tdo);
      let redactedMediaRecords = state.tdo?.redactedMediaAssets?.records || [];
      if (lastRedactedMediaAsset) {
        redactedMediaRecords = redactedMediaRecords.filter(
          (record) => record.id !== lastRedactedMediaAsset.id
        );
      }

      const lastAuditLogAsset = getLastAuditLog(state.tdo);
      let auditLogRecords = state.tdo?.auditLogAssets?.records || [];
      if (lastAuditLogAsset) {
        auditLogRecords = auditLogRecords.filter(
          (record) => record.id !== lastAuditLogAsset.id
        );
      }

      const lastExportAsset = getLastRedactExport(state.tdo);
      let redactExportRecords = state.tdo?.redactExportAssets?.records || [];
      if (lastExportAsset) {
        redactExportRecords = redactExportRecords.filter(
          (record) => record.id !== lastExportAsset.id
        );
      }

      state.tdo.redactedMediaAssets = Object.assign(
        state.tdo.redactedMediaAssets || {},
        {
          records: redactedMediaRecords,
        }
      );
      state.tdo.auditLogAssets = Object.assign(state.tdo.auditLogAssets || {}, {
        records: auditLogRecords,
      });
      state.tdo.redactExportAssets = Object.assign(
        state.tdo.redactExportAssets || {},
        {
          records: redactExportRecords,
        }
      );
    })
    .addCase(actions.STATUS_CHANGE, (state, action) => {
      let stateCopy = { ...state };
      const status = action.payload.status
        ? action.payload.status
        : DEFAULT_REDACTION_STATUS_LABEL;
      // Persist the human name + custom UUID (VE-24546). A programmatic dispatch
      // sends only the identity, so derive a built-in label from it.
      const writeFields: RedactionStatusTagWriteFields = action.payload
        .statusLabel
        ? {
            redactionStatus: action.payload.statusLabel,
            ...(action.payload.statusKey
              ? { redactionStatusKey: action.payload.statusKey }
              : {}),
          }
        : getBuiltInRedactionStatusTagWriteFields(status);

      // if no tdo exit
      if (!state.tdo || !stateCopy.tdo) {
        return state;
      }

      if (state.tdo.details?.tags) {
        let tag: Tag | null = null;
        const tagIndex = findLastIndex(
          state.tdo.details.tags,
          (item) => item?.value === actions.IN_REDACTION_TAG_KEY
        );

        if (tagIndex > -1) {
          tag =
            findLast(
              state.tdo.details.tags,
              (item) => item?.value === actions.IN_REDACTION_TAG_KEY
            ) ?? null;
        }

        const newTags = state.tdo.details.tags.slice();
        if (tag) {
          newTags[tagIndex] = mergeRedactionStatusTagFields(tag, writeFields);
        } else {
          newTags.push(
            mergeRedactionStatusTagFields(
              { value: actions.IN_REDACTION_TAG_KEY },
              writeFields
            )
          );
        }

        stateCopy = {
          ...stateCopy,
          tdo: {
            ...state.tdo,
            details: {
              ...state.tdo.details,
              tags: newTags,
            },
          },
        };
      } else {
        const newTag = mergeRedactionStatusTagFields(
          { value: actions.IN_REDACTION_TAG_KEY },
          writeFields
        );
        // TypeScript cannot ensure that every attribute of stateCopy copied from state is not null.
        if (stateCopy.tdo.details) {
          stateCopy.tdo.details.tags = [newTag];
        } else {
          stateCopy.tdo.details = {
            tags: [newTag],
            settings: DEFAULT_GLOBAL_SETTINGS,
          };
        }
      }

      return stateCopy;
    })
    .addCase(actions.FETCH_STATUS_CHANGE, (state) => ({
      ...state,
      fetchingUpdateStatus: true,
      failedUpdateStatus: false,
    }))
    .addCase(actions.FETCH_STATUS_CHANGE_SUCCESS, (state, action) => {
      const { updateTDO } = action.payload;

      // Update caseMedia if drawer is showing this case
      const caseMedia = state.caseMedia;
      if (caseMedia?.media) {
        const tdoIndex = caseMedia.media.findIndex(
          (tdo) => tdo.id === updateTDO.id
        );
        if (tdoIndex !== -1) {
          const tdo = caseMedia.media[tdoIndex];
          if (tdo) {
            tdo.modifiedDateTime = updateTDO.modifiedDateTime;
            if (updateTDO.details?.tags && tdo.details) {
              // @ts-expect-error Immer handles readonly conversion
              tdo.details.tags = updateTDO.details.tags;
            }
          }
        }
      }

      state.originalFileStatus = getOriginalFileStatus(updateTDO?.details);
      if (state.tdo) {
        state.tdo.modifiedDateTime = updateTDO.modifiedDateTime;
      }
      state.fetchingUpdateStatus = false;
      state.failedUpdateStatus = false;
    })
    .addCase(actions.FETCH_UPDATE_SETTINGS_SUCCESS, (state, action) => {
      const { updateTDO } = action.payload;
      /**
       * VE-30516: learn the two fields redux cannot know, and ONLY those.
       *
       * `isSaved` and `lastSavedDateTime` are added by the settings writer on
       * its way to the server, so they are absent from the action payload the
       * `SET_GLOBAL_SETTINGS` reducer mirrors — redux never had them, and every
       * read-modify-write of `details` used to push that gap back and delete
       * them. Every OTHER settings key is already in redux from that mirror.
       *
       * Taking the whole settings object back would therefore buy nothing and
       * cost correctness: `takeLatest` cancels the saga handler, not a thunk
       * already in flight, so two settings writes can be outstanding at once
       * and their responses can land out of order. Replacing wholesale lets the
       * older response revert a change the user has already seen applied.
       *
       * The stamp only ever moves FORWARD for the same reason it exists: it
       * feeds the stale-redacted-file warning (VE-25648), and a stamp that
       * slips backwards is the direction that hides a warning rather than
       * raising a spurious one.
       */
      const savedSettings = updateTDO?.details?.settings as
        | GlobalSettings
        | undefined;
      const currentSettings = state.tdo?.details?.settings;

      /** An unparseable stamp is no stamp, or it would block every later one. */
      const stampMs = (stamp: string | undefined) => {
        const ms = stamp ? new Date(stamp).getTime() : NaN;
        return Number.isNaN(ms) ? undefined : ms;
      };
      const savedStamp = savedSettings?.lastSavedDateTime;
      const savedMs = stampMs(savedStamp);
      const currentMs = stampMs(currentSettings?.lastSavedDateTime);
      const stampMovesForward =
        savedMs !== undefined &&
        (currentMs === undefined || savedMs > currentMs);

      /**
       * With nothing in redux to protect, take the whole object: merging only
       * the two server-owned fields onto `undefined` would leave settings with
       * just those, and `selectGlobalSettings` returns any truthy object rather
       * than falling back to the defaults — so `objectTypeEffects` and friends
       * would simply be gone for every consumer that indexes them.
       */
      const nextSettings = !currentSettings
        ? savedSettings
        : savedSettings && {
            ...currentSettings,
            ...(savedSettings.isSaved !== undefined && {
              isSaved: savedSettings.isSaved,
            }),
            ...(stampMovesForward && { lastSavedDateTime: savedStamp }),
          };

      return {
        ...state,
        originalFileStatus: getOriginalFileStatus(updateTDO?.details),
        tdo: state.tdo && {
          ...state.tdo,
          modifiedDateTime: updateTDO.modifiedDateTime,
          ...(nextSettings && {
            details: {
              ...state.tdo.details,
              settings: nextSettings,
            } as (typeof state.tdo)['details'],
          }),
        },
      };
    })
    .addCase(actions.FETCH_STATUS_CHANGE_FAILURE, (state) => ({
      ...state,
      fetchingUpdateStatus: false,
      failedUpdateStatus: true,
    }))
    .addCase(actions.PLAY_REDACTED_ASSET, (state) => {
      const { metrics } = getLastRedactedFile(state.tdo)?.details || {};
      if (metrics) {
        if (typeof metrics.views !== 'number') {
          metrics.views = 0;
        }
        metrics.views += 1;
      }
      state.redactedAssetStarted = true;
    })
    // download
    .addCase(actions.DOWNLOAD_ASSET, (state) => {
      const { metrics } = getLastRedactedFile(state.tdo)?.details || {};
      if (metrics) {
        if (typeof metrics.downloads !== 'number') {
          metrics.downloads = 0;
        }
        metrics.downloads += 1;
      }
      // start spinner when click download
      state.downloadStarted = true;
    })
    .addCase(actions.INCREMENT_DOWNLOAD_COUNT, (state) => {
      const { metrics } = getLastRedactedFile(state.tdo)?.details || {};
      if (metrics) {
        if (typeof metrics.downloads !== 'number') {
          metrics.downloads = 0;
        }
        metrics.downloads += 1;
      }
    })
    .addCase(actions.RETRY_DOWNLOAD, (state) => ({
      ...state,
      downloadStarted: true,
    }))
    .addCase(actions.UPDATE_DOWNLOAD_URL, (state, action) => ({
      ...state,
      downloadUrl: action.payload.downloadUrl,
    }))
    // 3 action to check status download in Media Detail
    .addCase(actions.DOWNLOAD_ASSET_JOB_STATUS, (state) => ({
      ...state,
      failedGettingDownloadJobStatus: false,
    }))
    .addCase(actions.DOWNLOAD_ASSET_JOB_STATUS_SUCCESS, (state, action) => ({
      ...state,
      downloadJobStatus: action.payload.job.status,
      failedGettingDownloadJobStatus: false,
    }))
    .addCase(actions.DOWNLOAD_ASSET_JOB_STATUS_FAILURE, (state) => ({
      ...state,
      failedGettingDownloadJobStatus: true,
    }))
    .addCase(actions.FETCH_DOWNLOAD_ASSET, (state) => ({
      ...state,
      fetchingCreateDownloadTask: true,
      pollingDownloadDone: false,
    }))
    .addCase(actions.FETCH_DOWNLOAD_ASSET_SUCCESS, (state) => ({
      ...state,
      fetchingCreateDownloadTask: false,
    }))
    .addCase(actions.FETCH_DOWNLOAD_ASSET_FAILURE, (state) => ({
      ...state,
      fetchingCreateDownloadTask: false,
    }))
    .addCase(actions.POLL_DOWNLOAD_ASSET, (state) => ({
      ...state,
      pollingDownloadDone: false,
    }))
    .addCase(actions.POLL_DOWNLOAD_ASSET_SUCCESS, (state, action) => ({
      ...state,
      auditLogs: get(action, 'payload.temporalDataObject.assets.records', []),
      failedGettingDownloadJobStatus: false,
      pollingDownloadDone: true,
      downloadJobStatus: false,
      downloadJob: null,
    }))
    .addCase(actions.POLL_DOWNLOAD_ASSET_FAILURE, (state) => ({
      ...state,
      pollingDownloadDone: true,
    }))
    .addCase(OUT_POLL_DOWNLOAD_ASSET_SUCCESS, (state, action) => {
      state.downloadStarted = false;
      if (!state.tdo) {
        return;
      }
      state.tdo.redactExportAssets = Object.assign(
        state.tdo.redactExportAssets || {},
        {
          records:
            action?.payload?.payload?.temporalDataObject?.assets.records || [],
        }
      );
      // pollingDownloadDone: true,
    })
    .addCase(OUT_POLL_DOWNLOAD_ASSET_FAILED, (state) => ({
      ...state,
      downloadStarted: false,
    }))
    .addCase(CREATE_JOB_FACES_ACTION, (state) => ({
      ...state,
      buttonDetectFaceDisable: true,
    }))
    .addCase(actions.UN_DISABLE_FACE_BUTTON, (state) => ({
      ...state,
      buttonDetectFaceDisable: false,
    }))
    .addCase(actions.DISABLE_FACE_BUTTON, (state) => ({
      ...state,
      buttonDetectFaceDisable: true,
    }))
    .addCase(PROCESS_ENGINE_TRANSCRIPTION_REQUEST, (state) => ({
      ...state,
      buttonTranscriptionDisable: true,
    }))
    // VE-30784: this does NOT clear the flag, it holds it.
    //
    // The run reporting success is also the moment the saga answering this
    // dispatches the transcript fetch, and the run's tasks stop reading as
    // running around now too — so clearing here left the audio tab on "No
    // engines have been run" for the length of the round trip.
    //
    // One flag rather than a second "results pending" one, because the tab and
    // the toolbar must not disagree: `TranscriptWrapperTabView` derives the Run
    // button's state from this, and a separate flag fed only to the tab put
    // "Transcribing Audio…" on screen beside an enabled, clickable Run button.
    // Its name is now a bit narrow — it means "a transcription is in progress",
    // of which "the button is disabled" is a consequence (the existing
    // `probably rename later for clarity` note in selectAudioTabState).
    //
    // Held only while no transcript is on screen: a re-run over an existing one
    // would otherwise replace it with a spinner. The fetch still runs.
    .addCase(PROCESS_ENGINE_TRANSCRIPTION_REQUEST_SUCCESS, (state) => ({
      ...state,
      buttonTranscriptionDisable: !state.transcription,
    }))
    // The fetch answered. Success reports through progress; failure through
    // OUT_TRANSCRIPTION_FETCH_FAILED, which deliberately leaves progress alone
    // (the page-load path reads "progress === 1 with no transcript" as "never
    // run"). Either way there is nothing left to wait for, and an empty answer
    // settles too — the poller only reports success once the run's output
    // writer is done.
    .addCase(OUT_PROGRESS, (state, action) =>
      action.payload.transcription === 1
        ? { ...state, buttonTranscriptionDisable: false }
        : state
    )
    .addCase(OUT_TRANSCRIPTION_FETCH_FAILED, (state) => ({
      ...state,
      buttonTranscriptionDisable: false,
    }))
    // Either outcome settles it: the audio tab is waiting to learn whether to
    // lay itself out for speakers, and a failed fetch answers that too.
    .addCase(OUT_SPEAKER_SEPARATION_FETCH_SETTLED, (state) => ({
      ...state,
      speakerSeparationFetchSettled: true,
    }))
    .addCase(OUT_CREATE_JOB_FAIL, (state, action) => {
      const { type } = action.payload;

      switch (type) {
        case FETCH_DOWNLOAD_ASSET_FAILURE.type:
          return {
            ...state,
            downloadStarted: false,
          };
        case FETCH_REDACT_TDO_FAILURE.type:
          return {
            ...state,
            gettingRedactionJobStatus: false,
          };
        case CREATE_JOB_FACES_FAILURE.type:
          return {
            ...state,
            buttonDetectFaceDisable: false,
          };
        case CREATE_ENGINE_JOB_TRANSCRIPTION_FAILURE.type:
          return {
            ...state,
            buttonTranscriptionDisable: false,
          };
        default:
          return { ...state };
      }
    })
    .addCase(actions.DISCARD_CHANGES, (state) => ({
      ...state,
      tdoIsChanged: false,
    }))
    //    VTN-10079 MDP Page not updating when Redact Engine Process Completes
    .addCase(actions.REDACTION_JOB_STATUS, (state) => ({
      ...state,
      gettingRedactionJobStatus: true,
      failedGettingRedactionJobStatus: false,
    }))
    .addCase(actions.REDACTION_JOB_STATUS_SUCCESS, (state, action) => ({
      ...state,
      redactionJobStatus: action.payload.job.status,
      redactionJobCreatedDateTime: action.payload.job.createdDateTime,
      failedGettingRedactionJobStatus: false,
    }))
    .addCase(actions.REDACTION_JOB_STATUS_FAILURE, (state) => ({
      ...state,
      gettingRedactionJobStatus: false,
      failedGettingRedactionJobStatus: true,
    }))
    .addCase(actions.REDACTION_JOB_STATUS_ACTION_STOP_POLLING, (state) => ({
      ...state,
      redactionJob: null,
      creatingRedactionJob: false,
      failedCreatingRedactionJob: false,
      failedGettingJobStatus: false,
    }))
    .addCase(OUT_FETCH_REDACT_TDO_SUCCESS, (state, action) => {
      const caseMedia = state.caseMedia;
      if (caseMedia?.media && state.tdo?.id) {
        const tdoIndex = caseMedia.media.findIndex(
          (tdo) => tdo.id === state.tdo?.id
        );

        if (tdoIndex !== -1) {
          const tdo = caseMedia.media[tdoIndex];

          if (tdo) {
            const newJob = {
              targetId: action.payload.createJob.id,
              status: 'running' as TaskStatus,
              createdDateTime: new Date().toISOString(),
            };

            if (tdo.jobs?.records) {
              // @ts-expect-error Immer handles readonly
              tdo.jobs.records = [newJob, ...tdo.jobs.records];
            } else if (tdo.jobs) {
              // @ts-expect-error Immer handles readonly
              tdo.jobs.records = [newJob];
            } else {
              // @ts-expect-error Immer handles readonly
              tdo.jobs = { records: [newJob] };
            }
          }
        }
      }

      state.redactionJob = action.payload.createJob;
      state.gettingRedactionJobStatus = false;
      state.redactedAssetStarted = false;
      state.redactionJobStatus = false;
      state.redactionJobCreatedDateTime = null;
      state.failedGettingRedactionJobStatus = false;
    })
    .addCase(actions.FETCH_REDACTED_MEDIA_SUCCESS, (state, action) => {
      if (state.tdo && action.payload) {
        const records =
          [] as typeof action.payload.temporalDataObject.redactedMediaAssets.records;
        const auditRecords =
          [] as typeof action.payload.temporalDataObject.auditLogAssets.records;
        const exportRecords =
          [] as typeof action.payload.temporalDataObject.redactExportAssets.records;

        Array.prototype.push.apply(
          records,
          get(
            action,
            'payload.temporalDataObject.redactedMediaAssets.records',
            []
          )
        );
        Array.prototype.push.apply(
          auditRecords,
          get(action, 'payload.temporalDataObject.auditLogAssets.records', [])
        );
        Array.prototype.push.apply(
          exportRecords,
          get(
            action,
            'payload.temporalDataObject.redactExportAssets.records',
            []
          )
        );
        // Filter out any IDs that were locally deleted but the server hasn't
        // propagated yet (racing the refetch). Once the server stops returning
        // them, remove them from the pending set so it stays clean.
        {
          const pending: string[] = state.pendingDeletedRedactedMediaIds ?? [];
          const serverIds = new Set(records.map((r: any) => r.id as string));
          const filteredRecords = records.filter(
            (r: any) => !pending.includes(r.id)
          );
          // Confirmed deleted = pending IDs that the server no longer returns.
          state.pendingDeletedRedactedMediaIds = pending.filter((id) =>
            serverIds.has(id)
          );
          if (state.tdo.redactedMediaAssets) {
            state.tdo.redactedMediaAssets.records = filteredRecords;
          } else {
            state.tdo.redactedMediaAssets = { records: filteredRecords };
          }
        }
        // Merge: keep any locally-added records that the server hasn't yet
        // propagated (e.g. an audit log uploaded moments ago), then re-sort
        // newest-first — the order the server itself returned. VE-27735: this
        // used to prepend the local-only records instead, which put anything
        // state held but the snapshot no longer returned (a rotated-away log) at
        // index 0 and masked the newer record behind it. A record with no
        // timestamp was just created and not yet read back, so it sorts newest.
        // Filter out pending-deleted IDs so a racing refetch can't re-introduce
        // audit logs we just deleted.
        {
          const pending: string[] = state.pendingDeletedAuditLogIds ?? [];
          const serverIds = new Set(
            auditRecords.map((r: any) => r.id as string)
          );
          const filteredAuditRecords = auditRecords.filter(
            (r: any) => !pending.includes(r.id)
          );
          state.pendingDeletedAuditLogIds = pending.filter((id) =>
            serverIds.has(id)
          );
          const existing = state.tdo.auditLogAssets?.records ?? [];
          const localOnly = existing.filter((r: any) => !serverIds.has(r.id));
          // MAX_SAFE_INTEGER rather than Infinity: two un-timestamped records
          // would subtract to NaN, and a NaN comparator leaves the order to the
          // engine rather than to the stable sort.
          const burnedInAt = (record: { createdDateTime?: string }) =>
            record.createdDateTime
              ? new Date(record.createdDateTime).getTime()
              : Number.MAX_SAFE_INTEGER;
          const merged = [...localOnly, ...filteredAuditRecords].sort(
            (a, b) => burnedInAt(b) - burnedInAt(a)
          );
          if (state.tdo.auditLogAssets) {
            state.tdo.auditLogAssets.records = merged;
          } else {
            state.tdo.auditLogAssets = { records: merged };
          }
        }
        if (state.tdo.redactExportAssets) {
          state.tdo.redactExportAssets.records = exportRecords;
        } else {
          state.tdo.redactExportAssets = { records: exportRecords };
        }
        // VE-25870: the burn-in that just landed points at the
        // redaction-user-selections asset it consumed, and only that asset names
        // the redaction data behind it. Redact File writes that asset just before
        // sending the job, so it postdates page load by definition and the list
        // fetched then cannot hold it. It dates the burn-in, which is what a
        // settings change is measured against, so without this a settings save
        // made in this session is compared to the previous run.
        const selectionRecords = get(
          action,
          'payload.temporalDataObject.redactionSelectionAssets.records',
          []
        ) as NonNullable<typeof state.tdo.redactionSelectionAssets>['records'];
        if (selectionRecords.length > 0) {
          state.tdo.redactionSelectionAssets = { records: selectionRecords };
        }
      }
      state.gettingRedactionJobStatus = false;
      state.redactionJobStatus = false;
      state.redactionJobCreatedDateTime = null;
      state.redactionJob = null;
    })
    .addCase(actions.SHOW_BLOCK_NAVIGATION_MODAL, (state, action) => ({
      ...state,
      isShowConfirmLeave: true,
      message: action.payload.message,
      canLeave: action.payload.canLeave,
    }))
    .addCase(actions.HIDE_BLOCK_NAVIGATION_MODAL, (state) => ({
      ...state,
      isShowConfirmLeave: false,
      message: '',
      canLeave: null,
    }))
    .addCase(actions.ACTION_KEY_SHIFT, (state, action) => ({
      ...state,
      isHoldingShift: action.payload.isKeyDown,
    }))
    .addCase(actions.MEDIA_HAS_AUDIO, (state, action) => ({
      ...state,
      hasAudio: action.payload.hasAudio,
    }))
    .addCase(actions.VIDEO_HAS_LOADED, (state, action) => ({
      ...state,
      videoLoaded: action.payload.videoLoaded,
    }))
    .addCase(actions.SET_THUMBNAIL_TRACK, (state, action) => ({
      ...state,
      thumbnailTracks: action.payload.thumbnailTracks,
    }))
    .addCase(actions.SET_SHAKA_PLAYER, (state, action) => ({
      ...state,
      shakaPlayer: action.payload.shakaPlayer || undefined, // TODO: Should this be null or undefined?
    }))
    .addCase(
      actions.SET_GOVQA_SESSIONID,
      (state, { payload: { sessionId } }) => ({
        ...state,
        integrations: {
          ...state.integrations,
          govQa: {
            ...state.integrations.govQa,
            sessionId: sessionId || undefined,
          },
        },
      })
    )
    .addCase(
      actions.PATCH_EXTERNAL_INTEGRATION_STATE,
      (state, { payload }) => ({
        ...state,
        integrations: {
          ...state.integrations,
          externalIntegration: {
            ...state.integrations.externalIntegration,
            ...payload,
          },
        },
      })
    )
    .addCase(actions.PATCH_GOVQA_STATE, (state, { payload }) => ({
      ...state,
      integrations: {
        ...state.integrations,
        govQa: {
          ...state.integrations.govQa,
          ...payload,
        },
      },
    }))
    .addCase(actions.PATCH_FOIAXPRESS_STATE, (state, { payload }) => ({
      ...state,
      integrations: {
        ...state.integrations,
        foiaXpress: {
          ...state.integrations.foiaXpress,
          ...payload,
        },
      },
    }))
    .addCase(actions.PATCH_CASEPOINT_STATE, (state, { payload }) => ({
      ...state,
      integrations: {
        ...state.integrations,
        casepoint: {
          ...state.integrations.casepoint,
          ...payload,
        },
      },
    }))
    .addCase(actions.PATCH_EXTERRO_STATE, (state, { payload }) => ({
      ...state,
      integrations: {
        ...state.integrations,
        exterro: {
          ...state.integrations.exterro,
          ...payload,
        },
      },
    }))
    .addCase(actions.PATCH_NUIX_STATE, (state, { payload }) => ({
      ...state,
      integrations: {
        ...state.integrations,
        nuix: {
          ...state.integrations.nuix,
          ...payload,
        },
      },
    }))
    .addCase(
      actions.SET_SELECTED_UDR_GROUP,
      (state, { payload: { groupId } }) => {
        let newState = {
          ...state,
          udrsState: {
            ...state.udrsState,
            selectedUDRGroupId: groupId,
          },
        };

        const highlightedOverlay = state.highlightedOverlay;

        if (
          groupId &&
          highlightedOverlay?.id &&
          highlightedOverlay.groupId !== groupId
        ) {
          newState = {
            ...newState,
            highlightedOverlay: undefined,
          };
        }

        return newState;
      }
    )
    .addCase(actions.SET_IS_SAVE_RUNNING, (state, action) => ({
      ...state,
      isSaveRunning: action?.payload?.isSaveRunning,
    }))
    .addCase(actions.SET_BANNER_DISMISSED, (state, action) => ({
      ...state,
      isBannerDismissed: action.payload,
    }))
    .addCase(actions.SET_PENDING_LOCKED_STATUS, (state, action) => ({
      ...state,
      pendingLockedStatus: action.payload.status,
      pendingLockedStatusNote: action.payload.note ?? null,
      pendingLockedStatusResponse: action.payload.response ?? null,
      pendingLockedStatusResponseType: action.payload.responseType ?? null,
    }))
    .addCase(actions.CLEAR_PENDING_LOCKED_STATUS, (state) => ({
      ...state,
      pendingLockedStatus: null,
      pendingLockedStatusNote: null,
      pendingLockedStatusResponse: null,
      pendingLockedStatusResponseType: null,
    }))
    .addCase(actions.TOGGLE_CASE_MEDIA_DRAWER, (state) => ({
      ...state,
      isCaseMediaDrawerOpen: !state.isCaseMediaDrawerOpen,
    }))
    // Routing to a document doesn't reset this slice the way ROUTE_MEDIA_DETAILS
    // does, so the case-media drawer would otherwise stay open. Close it here so
    // documents match the video flow. The switch-reset fields go with it: the
    // drawer used to dispatch RESET_MEDIA_DETAILS_ON_SWITCH before the route
    // action, which wiped job/status state even when the reviewer then
    // cancelled the leave prompt (VE-30855). Here it only runs once the
    // navigation commits. The document editor reads none of these fields.
    .addCase(ROUTE_DOCUMENT_DETAILS, (state) => ({
      ...state,
      ...switchResetFields,
      isCaseMediaDrawerOpen: false,
    }))
    .addCase(actions.RESET_MEDIA_DETAILS_ON_SWITCH, (state) => ({
      ...state,
      ...switchResetFields,
    }))
    .addCase(actions.DELETE_CASE_DRAWER_TDO_SUCCESS, (state, action) => ({
      ...state,
      caseMedia: {
        ...state.caseMedia,
        media: [
          ...((state.caseMedia?.media || []).filter(
            (file) => file.id !== action?.payload?.tdoId
          ) || []),
        ],
      },
    }))
    .addCase(actions.DELETE_DETECTION_OVERLAY, onUpdateDeletedOverlaysCache)
    .addCase(actions.DELETE_DETECTION_IN_FRAME, onUpdateDeletedOverlaysCache)
    .addCase(actions.DELETE_DETECTION_SEGMENT, onUpdateDeletedOverlaysCache)
    .addCase(actions.DELETE_DETECTION_GROUP, onUpdateDeletedOverlaysCache)
    .addCase(actions.DELETE_UDR_OVERLAY, onUpdateDeletedOverlaysCache)
    .addCase(actions.DELETE_UDR_IN_FRAME, onUpdateDeletedOverlaysCache)
    .addCase(actions.DELETE_UDR_SEGMENT, onDeleteUDRSegment)
    .addCase(actions.DELETE_UDR_GROUP, onDeleteUDRGroup)
    .addCase(actions.DELETE_CLUSTER, onUpdateDeletedOverlaysCache)
    .addCase(actions.SET_DELETED_OVERLAYS_CACHE, onSetDeletedOverlaysCache)
    // @ts-expect-error TODO: Fix types
    .addCase(actions.FETCH_CASE_MEDIA_SUCCESS, fetchCaseMediaSuccess)
    // @ts-expect-error TODO: Fix types
    .addCase(actions.FETCH_TDO_SUCCESS, onFetchTDOSuccess)
    .addCase(actions.TDO_EXTERNALLY_MODIFIED, (state) => ({
      ...state,
      isTdoExternallyModified: true,
    }))
    .addCase(actions.SELECT_COMMENT, selectComment)
    .addCase(actions.SELECT_COMMENT_ON_TIMELINE, selectCommentsOnTimeline)
    .addCase(actions.REMOVE_COMMENT, removeComment)
    .addCase(actions.ADD_NEW_COMMENT, addNewComment)
    .addCase(actions.SET_MEDIA_COMMENT_DRAFT, setMediaCommentDraft)
    .addCase(actions.CLEAR_MEDIA_COMMENT_DRAFT, clearMediaCommentDraft)
    .addCase(actions.SET_EDITING_MEDIA_COMMENT, setEditingMediaComment)
    .addCase(actions.UPDATE_STATUS_HIDE_COMMENT, updateStatusHideComment)
    .addCase(
      actions.FETCH_MEDIA_COMMENTS_USER_SUCCESS,
      fetchMediaCommentsUserSuccess
    )
    .addCase(actions.FETCH_MEDIA_COMMENTS, fetchMediaComments)
    .addCase(actions.UPDATE_COMMENT_SUCCESS, updateCommentSuccess)
    .addCase(actions.FETCH_MEDIA_COMMENTS_SUCCESS, fetchMediaCommentsSuccess)
    .addCase(actions.FETCH_MEDIA_USER_SUCCESS, fetchMediaUserSuccess)
    .addCase(actions.BASIC_USER_QUERY_IS_SUPPORTED, basicUserQueryIsSupported)
    .addCase(actions.TDO_NAME_CHANGE_SUCCESS, (state, action) => {
      const { updateTDO } = action.payload;
      if (!state.tdo || state.tdo.id !== updateTDO.id) {
        console.error('TDO not found while updating name');
        return state;
      }

      return {
        ...state,
        tdo: {
          ...state.tdo,
          ...updateTDO,
        },
      };
    })
    .addCase(actions.FETCH_UPDATE_TDO_DETAILS_SUCCESS, (state, action) => {
      const { updateTDO } = action.payload;
      if (!state.tdo || state.tdo.id !== updateTDO.id) {
        return;
      }
      state.tdo.modifiedDateTime = updateTDO.modifiedDateTime;
      // @ts-expect-error Immer handles readonly conversion
      state.tdo.details = updateTDO.details;
    })
    .addCase(actions.ADD_AUDIT_LOG_ASSET_TO_TDO, (state, action) => {
      if (!state.tdo) {
        return;
      }
      const asset = action.payload;
      const existing = state.tdo.auditLogAssets?.records ?? [];
      // Drop any prior record with the same id so re-dispatching is idempotent.
      const filtered = existing.filter((a) => a.id !== asset.id);
      state.tdo.auditLogAssets = { records: [asset, ...filtered] };
    })
    .addCase(actions.ADD_REDACTED_MEDIA_ASSET_TO_TDO, (state, action) => {
      if (!state.tdo) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const asset = action.payload as any;
      const existing = state.tdo.redactedMediaAssets?.records ?? [];
      const filtered = existing.filter((a: any) => a.id !== asset.id);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      state.tdo.redactedMediaAssets = { records: [asset, ...filtered] as any };
    })
    .addCase(actions.REMOVE_REDACTED_MEDIA_ASSETS_FROM_TDO, (state, action) => {
      if (!state.tdo) {
        return;
      }
      const ids = new Set(action.payload);
      const existing = state.tdo.redactedMediaAssets?.records ?? [];

      state.tdo.redactedMediaAssets = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        records: existing.filter((a: any) => !ids.has(a.id)) as any,
      };
      // Track pending deletions so FETCH_REDACTED_MEDIA_SUCCESS doesn't
      // re-introduce them if the refetch races the server-side delete.
      const pendingSet = new Set(state.pendingDeletedRedactedMediaIds ?? []);
      for (const id of action.payload) {
        pendingSet.add(id);
      }
      state.pendingDeletedRedactedMediaIds = [...pendingSet];
    })
    .addCase(actions.REMOVE_AUDIT_LOG_ASSETS_FROM_TDO, (state, action) => {
      if (!state.tdo) {
        return;
      }
      const ids = new Set(action.payload);
      const existing = state.tdo.auditLogAssets?.records ?? [];
      state.tdo.auditLogAssets = {
        records: existing.filter((a) => !ids.has(a.id)),
      };
      const pendingSet = new Set(state.pendingDeletedAuditLogIds ?? []);
      for (const id of action.payload) {
        pendingSet.add(id);
      }
      state.pendingDeletedAuditLogIds = [...pendingSet];
    })
    .addCase(ON_CHANGE_DETECT_OBJECTS, (state, action) => {
      state.detectObjects[action.payload.name] = action.payload.value;
    })
    .addCase(SHOW_DETECT_OPTIONS_DIALOG, (state, action) => ({
      ...state,
      showDetectOptionsDialog: action.payload.value,
    }))
    .addCase(CREATE_JOB_FACES, (state) => ({
      ...state,
      detectObjects: {
        [DETECT_OPTIONS.HEAD_OBJECTS]: false,
        [DETECT_OPTIONS.PERSON_OBJECTS]: false,
      },
    }))
    .addCase(
      actions.SET_IS_PLAYER_SEEKED,
      (state, { payload: isPlayerSeeked }) => ({
        ...state,
        isPlayerSeeked,
      })
    )
    .addCase(FETCH_INTEGRATION_INFO_SUCCESS, (state, { payload }) => ({
      ...state,
      integrations: {
        ...state.integrations,
        externalIntegration: {
          ...state.integrations.externalIntegration,
          config: payload.me.organization.integrationConfig?.config || {},
        },
      },
    }))
    // TODO might want to store the schemaId so can be retrieved faster
    // .addCase(actions.SECURE_TDO_LOCK_START, (state, { payload }) => ({
    //   ...state,
    //   tdoLockRegistryId: payload.variables.schemaId
    // }))
    .addCase(actions.SECURE_TDO_LOCK_SUCCESS, (state, { payload }) => ({
      ...state,
      tdoLocks: [{ id: payload.createStructuredData.id }],
    }))
    .addCase(actions.UPDATE_TDO_LOCKS, (state, { payload }) => ({
      ...state,
      tdoLocks: payload.tdoLocks,
    }))
    .addCase(actions.REDACTION_DATA_BURN_IN_LABELLED, (state, { payload }) => {
      const record = state.tdo?.redactionDataAssets?.records?.find(
        (asset) => asset.id === payload.assetId
      );
      if (record) {
        record.details = {
          ...record.details,
          burnInRequested: true,
          expectedRedactedVersion: payload.expectedRedactedVersion,
        };
      }
    });
});
